/**
 * Audit Logs API Client
 * API functions for the audit log endpoints
 */

import { formatDateToISO } from '../../utils/formatters';
import apiClient from '../client';

/**
 * Fetch audit logs with filters
 */
export const getAuditLogs = async ({
  userId,
  entityType,
  entityId,
  eventType,
  module,
  dateFrom,
  dateTo,
  search,
  limit = 50,
  offset = 0
} = {}) => {
  try {
    const params = new URLSearchParams();

    if (userId) params.append('userId', userId);
    if (entityType) params.append('entityType', entityType);
    if (entityId) params.append('entityId', entityId);
    if (eventType) params.append('eventType', eventType);
    if (module) params.append('module', module);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (search) params.append('search', search);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await apiClient.get(`/wholesale/audit-logs?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

/**
 * Get history for a specific entity
 */
export const getEntityHistory = async (entityType, entityId) => {
  try {
    const response = await apiClient.get(`/wholesale/audit-logs/entity/${entityType}/${entityId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching entity history:', error);
    throw error;
  }
};

/**
 * Get activity for a specific user
 */
export const getUserActivity = async (userId, limit = 100) => {
  try {
    const response = await apiClient.get(`/wholesale/audit-logs/user/${userId}?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user activity:', error);
    throw error;
  }
};

/**
 * Get audit statistics
 */
export const getAuditStats = async ({ dateFrom, dateTo, module } = {}) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (module) params.append('module', module);

    const response = await apiClient.get(`/wholesale/audit-logs/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit stats:', error);
    throw error;
  }
};

/**
 * Export audit logs
 */
export const exportAuditLogs = async (filters = {}, format = 'csv') => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    params.append('format', format);

    const response = await apiClient.get(`/wholesale/audit-logs/export?${params.toString()}`, {
      responseType: 'blob'
    });

    // Create download link
    const blob = new Blob([response.data], {
      type: format === 'json' ? 'application/json' : 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_logs_${formatDateToISO(new Date())}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error exporting audit logs:', error);
    throw error;
  }
};

export default {
  getAuditLogs,
  getEntityHistory,
  getUserActivity,
  getAuditStats,
  exportAuditLogs
};
